import { useEffect, useMemo, useState } from 'react';
import { runInAction } from 'mobx';
import { useRouter } from 'next/router';
import { Grid } from 'theme-ui';
import styles from '@features/cms/components/pages/ProductsListing/ProductsListing.styles';
import { WindowType } from '@typescript/index';
import ErrorBoundary from '@components/errors/ErrorBoundary';
import SeeMoreProducts from '@features/cms/components/pages/SeeMoreProducts/SeeMoreProducts';
import ProductState from '@features/categories/mobx/state/ProductState';
import { IInspirationProduct } from '@features/cms/typescript';
import ProductList from '@components/ProductList/ProductList';
import LayoutUiState from '@features/layout/mobX/LayoutUiState';
import { buildListingPageHref, getListingPageFromQuery, setListingPageInUrl } from '@utils/listingPaginationUrl';
const PageSizeOnMobile = 4;
const PageSizeOnDesktop = 8;
type PropsType = {
  products: IInspirationProduct[];
  windowType: WindowType;
};
const ProductsListing = ({
  products,
  windowType
}: PropsType) => {
  const router = useRouter();
  const isMobile = windowType === WindowType.mobile;
  const isTablet = windowType === WindowType.tablet;
  const isSmallerThanDesktop = isTablet || isMobile;
  const pageSize = isSmallerThanDesktop ? PageSizeOnMobile : PageSizeOnDesktop;
  const pageFromUrl = getListingPageFromQuery(router.query.page);
  const maxPage = Math.max(1, Math.ceil(products.length / pageSize) || 1);

  // SSR + first client paint: pageSize only (router.query may be empty on SSR for static pages)
  const [loadedNumberProducts, setLoadedNumberProducts] = useState(pageSize);
  const currentPage = Math.max(1, Math.ceil(loadedNumberProducts / pageSize) || 1);
  const showMoreItems = loadedNumberProducts < products.length;
  const nextPage = Math.min(currentPage + 1, maxPage);
  const visibleProducts = useMemo(() => {
    const productsStage: ProductState[] = [];
    if (!products?.length) return productsStage;
    runInAction(() => {
      products.forEach((singleProduct, index) => {
        if (index < loadedNumberProducts && singleProduct?.id) {
          productsStage.push(new ProductState(singleProduct));
        }
      });
    });
    return productsStage;
  }, [products, loadedNumberProducts]);
  const resolveLoadedForPage = (page: number) => {
    let loaded = Math.min(page * pageSize, products.length);

    // Ensure returned-to product is visible (same idea as category scroll restore)
    const lastVisitedProductId = LayoutUiState.lastVisitedProductId;
    if (lastVisitedProductId) {
      const productIndex = products.findIndex(product => product?.id === lastVisitedProductId);
      if (productIndex >= 0) {
        const needed = Math.ceil((productIndex + 1) / pageSize) * pageSize;
        loaded = Math.max(loaded, Math.min(needed, products.length));
      }
    }
    return loaded;
  };
  useEffect(() => {
    if (!router.isReady) return;
    const page = Math.min(pageFromUrl, maxPage);
    const nextLoaded = resolveLoadedForPage(page);
    setLoadedNumberProducts(nextLoaded);
    const pageAfterResolve = Math.max(1, Math.ceil(nextLoaded / pageSize) || 1);
    if (pageAfterResolve !== pageFromUrl) {
      setListingPageInUrl(pageAfterResolve);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from URL / breakpoint / product list
  }, [router.isReady, pageFromUrl, pageSize, products.length, maxPage]);
  const handleShowMoreProducts = () => {
    const newPage = Math.min(currentPage + 1, maxPage);
    const newLoaded = Math.min(newPage * pageSize, products.length);
    setLoadedNumberProducts(newLoaded);
    setListingPageInUrl(newPage);
  };
  const renderContent = () => <>
      <Grid columns={24} sx={styles.productsRow}>
        <ProductList products={visibleProducts} productsCount={products.length} listingType="products_listing" />
      </Grid>
      <div sx={styles.container}>
        <SeeMoreProducts showMoreItems={showMoreItems} loadedNumberProducts={loadedNumberProducts} maxCount={products.length} nextPageHref={buildListingPageHref(router.asPath, nextPage)} handleShowMoreProducts={handleShowMoreProducts} />
      </div>
    </>;
  return products.length > 0 ? <ErrorBoundary>
      <section id="products" sx={styles.section}>
        {renderContent()}
      </section>
    </ErrorBoundary> : null;
};
export default ProductsListing;